import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import theme from '../../styled-components/dark';

const TAB_HEIGHT = 1.5;
const ANIMATION_DURATION = 200;

export const TabsWrapperClassName = 'tabs-wrapper';
type WrapperPropsType = {
  $tabHeight: number;
  $defaultBackgroundColor?: string;
};
const Wrapper = styled.div<WrapperPropsType>`
  position: relative;

  ${({ $tabHeight, $defaultBackgroundColor }) => css`
    height: ${$tabHeight}em;
    line-height: ${$tabHeight}em;
    border-radius: ${$tabHeight}em;
    background-color: var(
      --Tabs-defaultBackgroundColor,
      ${$defaultBackgroundColor ?? theme.Tabs.default.backgroundColor}
    );
  `};

  width: 100%;

  display: flex;
  flex-direction: row;
  align-items: center;
`;

type TabPropsType = {
  $selected: boolean;
  $tabHeight: number;
  $selectedColor?: string;
  $defaultColor?: string;
  $disabled?: boolean;
};
const Tab = styled.div<TabPropsType>`
  position: relative;
  width: 100%;
  text-align: center;
  margin: 0em 0.4em;
  display: flex;
  align-items: center;
  justify-content: center;

  font-weight: bold;
  font-size: 0.95em;

  ${({ $tabHeight }) => css`
    height: ${$tabHeight}em;
    line-height: ${$tabHeight}em;
  `};

  z-index: 2;
  transition: color ${ANIMATION_DURATION}ms ease-in-out;

  ${({ $selected, $selectedColor, $defaultColor, $disabled }) => {
    const overridingStyle = css`
      ${$disabled
        ? css`
            filter: grayscale(1) brightness(0.6);
            pointer-events: none;
          `
        : undefined}
    `;

    if ($selected) {
      return css`
        color: var(--Tabs-selectedColor, ${$selectedColor ?? theme.Tabs.selected.color});
        pointer-events: none;
        ${overridingStyle}
      `;
    }

    return css`
      color: var(--Tabs-defaultColor, ${$defaultColor ?? theme.Tabs.default.color});
      cursor: pointer;
      ${overridingStyle}
    `;
  }}
`;

export const TabsSelectedClassName = 'tabs-selected';
type SelectedPropsType = {
  $width: number;
  $position: number;
  $tabHeight: number;
};
const Selected = styled.div<SelectedPropsType>`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
  transition: transform ${ANIMATION_DURATION}ms ease-in-out;

  overflow: hidden;

  ${({ $width, $position, $tabHeight }) => {
    if ($width) {
      return css`
        width: ${$width}%;
        height: ${$tabHeight}em;
        border-radius: ${$tabHeight}em;
        transform: translateX(${$position * 100}%);
      `;
    }
  }};
`;

export const TabsSelectedBackgroundClassName = 'tabs-background-selected';
type SelectedBackgroundPropsType = {
  $selectedBackgroundColor?: string;
};
const SelectedBackground = styled.div<SelectedBackgroundPropsType>`
  position: absolute;
  left: 50%;
  top: 50%;
  width: calc(100% - 4px);
  height: calc(100% - 4px);

  transform: translate(-50%, -50%);
  border-radius: inherit;

  ${({ $selectedBackgroundColor }) => {
    return css`
      background-color: var(
        --Tabs-selectedBackgroundColor,
        ${$selectedBackgroundColor ?? theme.Tabs.selected.backgroundColor}
      );
    `;
  }};
`;

type TabType = { id: string | number; icon?: React.ReactNode; label?: string; disabled?: boolean };

type TabsPropsType = {
  tabs: Array<TabType>;
  selected?: string | number;
  onSelectionChange?: (selectedId: string | number) => void;
  className?: string;
  tabHeight?: number;
  customStyle?: Partial<{
    defaultBgColor?: string;
    defaultColor?: string;
    selectedBgColor?: string;
    selectedColor?: string;
  }>;
};

const Tabs = ({
  tabs = [],
  onSelectionChange = () => {},
  selected = tabs?.[0]?.id,
  className = '',
  tabHeight = TAB_HEIGHT,
  customStyle: { defaultBgColor, defaultColor, selectedBgColor, selectedColor } = {},
}: TabsPropsType) => {
  const tabsLength = useMemo(() => tabs.length, [tabs]);
  const selectedPosition = useMemo(() => tabs.findIndex((tab) => tab.id === selected), [tabs, selected]);

  const onTabClick = useCallback(
    (tabId: string | number) => (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (selected !== tabId) {
        onSelectionChange(tabId);
      }
    },
    [onSelectionChange, selected],
  );

  return (
    <Wrapper
      className={`${TabsWrapperClassName} ${className}`}
      $tabHeight={tabHeight}
      $defaultBackgroundColor={defaultBgColor}
    >
      {tabs.map((tab) => (
        <Tab
          key={`tab-${tab.id}`}
          $selected={tab.id === selected}
          onClick={onTabClick(tab.id)}
          $tabHeight={tabHeight}
          $selectedColor={selectedColor}
          $defaultColor={defaultColor}
          $disabled={tab.disabled}
        >
          {tab.icon ? tab.icon : null}
          {tab.label ? tab.label : null}
        </Tab>
      ))}
      <Selected $position={selectedPosition} $width={100 / tabsLength} $tabHeight={tabHeight}>
        <SelectedBackground className={TabsSelectedBackgroundClassName} $selectedBackgroundColor={selectedBgColor} />
      </Selected>
    </Wrapper>
  );
};

export default React.memo<TabsPropsType>(Tabs);
